import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Gift } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const [mode, setMode] = useState(refCode ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { ...form, referral_code: refCode || undefined, origin: window.location.origin };
      const { data } = await api.post(endpoint, payload);
      login(data);
      toast.success(mode === "login" ? "Welcome back to the Empire" : "Membership activated");
      navigate(mode === "login" ? "/discover" : "/profile");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10 flex items-center justify-center p-6" style={{ background: "#0A0A0A" }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md pe-card rounded-3xl p-8">
        <Link to="/" className="block text-center mb-8">
          <Flame className="text-[#D4AF37] mx-auto mb-2" size={32} />
          <div className="font-heading text-3xl gold-text">Pleasure Empire</div>
          <div className="overline mt-1">Members Only · 18+</div>
        </Link>

        <div className="flex gap-2 mb-6 bg-[#1A1A1A] rounded-full p-1">
          {["login", "register"].map((m) => (
            <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${mode === m ? "gold-gradient text-black" : "text-[#A0A0A0]"}`}>
              {m === "login" ? "Sign In" : "Join"}
            </button>
          ))}
        </div>

        {refCode && mode === "register" && (
          <div data-testid="ref-banner" className="mb-5 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3 flex items-center gap-2 text-sm text-[#D4AF37]">
            <Gift size={16} /> You were invited! Join now to claim <b>50 bonus Empire Coins</b>.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="input-name" required placeholder="Display name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="pe-input w-full rounded-lg px-4 py-3" />
          )}
          <input data-testid="input-email" required type="email" placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="pe-input w-full rounded-lg px-4 py-3" />
          <input data-testid="input-password" required type="password" placeholder="Password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="pe-input w-full rounded-lg px-4 py-3" />
          <button data-testid="submit-auth" disabled={loading} type="submit"
            className="btn-gold w-full rounded-full py-3 disabled:opacity-60">
            {loading ? "Please wait…" : mode === "login" ? "Enter the Empire" : "Claim membership"}
          </button>
        </form>

        <div className="mt-6">
          <div className="text-center text-xs text-[#A0A0A0] mb-3">or continue with</div>
          <button data-testid="google-btn" type="button" onClick={googleLogin}
            className="btn-outline-gold w-full rounded-full py-3 flex items-center justify-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#D4AF37" d="M21.35 11.1h-9.17v2.98h5.27c-.23 1.24-.94 2.29-2 3l3.24 2.51c1.89-1.75 2.98-4.32 2.98-7.39 0-.66-.06-1.29-.17-1.9z"/><path fill="#D4AF37" d="M12.18 22c2.7 0 4.96-.9 6.62-2.41l-3.24-2.51c-.9.6-2.05.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12L3.24 16.5C4.89 19.78 8.27 22 12.18 22z"/><path fill="#D4AF37" d="M6.59 13.92A5.99 5.99 0 0 1 6.27 12c0-.67.12-1.32.32-1.92L3.24 7.5A9.98 9.98 0 0 0 2.18 12c0 1.61.39 3.14 1.06 4.5l3.35-2.58z"/><path fill="#D4AF37" d="M12.18 5.96c1.47 0 2.79.51 3.83 1.5l2.87-2.87C17.13 2.99 14.88 2 12.18 2 8.27 2 4.89 4.22 3.24 7.5l3.35 2.58c.79-2.36 2.99-4.12 5.59-4.12z"/></svg>
            Continue with Google
          </button>
        </div>
      </motion.div>
    </div>
  );
}
